/*
 * Rejuvenation PDP redesign
 *
 * Three small custom elements:
 *   <rejuv-accordion>  disclosure used in the buy column, formula list and FAQ
 *   <rejuv-buy>        option / selling-plan / quantity state for the buy box
 *   <rejuv-sticky>     reveals the sticky add-to-cart bar past a scroll offset
 *
 * The add-to-cart request itself is handled by the theme's existing
 * <product-form> element, so the cart drawer keeps working unchanged.
 */

(function () {
  'use strict';

  /*
   * This shop's money_format is HTML (`<span class=money>${{amount}} USD</span>`).
   * The section strips the markup before handing it over, but guard anyway:
   * these values are written with textContent, so a format containing markup
   * would render as literal tags.
   */
  function formatMoney(cents, format) {
    var safe = format && format.indexOf('<') === -1 ? format : '${{amount}}';
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, safe);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  if (!customElements.get('rejuv-accordion')) {
    customElements.define(
      'rejuv-accordion',
      class RejuvAccordion extends HTMLElement {
        connectedCallback() {
          this.button = this.querySelector('.rejuv-acc__btn');
          this.panel = this.querySelector('.rejuv-acc__panel');
          if (!this.button || !this.panel) return;

          // The group name lets a set of accordions behave as an exclusive
          // group (formula + FAQ), matching the design's single-open lists.
          this.group = this.dataset.group || '';
          this.button.addEventListener('click', this.onClick.bind(this));
        }

        onClick() {
          const willOpen = this.button.getAttribute('aria-expanded') !== 'true';

          if (willOpen && this.group) {
            const scope = this.closest('.rejuv') || document;
            scope
              .querySelectorAll('rejuv-accordion[data-group="' + this.group + '"]')
              .forEach((other) => {
                if (other !== this) other.setOpen(false);
              });
          }

          this.setOpen(willOpen);
        }

        setOpen(open) {
          if (!this.button || !this.panel) return;
          this.button.setAttribute('aria-expanded', open ? 'true' : 'false');
          this.panel.hidden = !open;
        }
      }
    );
  }

  if (!customElements.get('rejuv-lang-toggle')) {
    customElements.define(
      'rejuv-lang-toggle',
      class RejuvLangToggle extends HTMLElement {
        connectedCallback() {
          this.buttons = Array.from(this.querySelectorAll('[data-rejuv-lang]'));
          this.panels = Array.from(this.querySelectorAll('[data-rejuv-lang-panel]'));
          if (!this.buttons.length || !this.panels.length) return;

          this.buttons.forEach((button) => {
            button.addEventListener('click', () => this.select(button.dataset.rejuvLang));
          });

          this.select(this.dataset.default || this.buttons[0].dataset.rejuvLang);
        }

        select(lang) {
          this.buttons.forEach((button) => {
            button.setAttribute('aria-pressed', button.dataset.rejuvLang === lang ? 'true' : 'false');
          });
          this.panels.forEach((panel) => {
            panel.hidden = panel.dataset.rejuvLangPanel !== lang;
          });
        }
      }
    );
  }

  if (!customElements.get('rejuv-sticky')) {
    customElements.define(
      'rejuv-sticky',
      class RejuvSticky extends HTMLElement {
        connectedCallback() {
          this.threshold = parseInt(this.dataset.threshold, 10) || 900;
          this.bar = this.querySelector('.rejuv-sticky');
          if (!this.bar) return;

          this.onScroll = this.onScroll.bind(this);
          this.onResize = () => { if (this.shown) this.publishClearance(); };
          window.addEventListener('scroll', this.onScroll, { passive: true });
          window.addEventListener('resize', this.onResize, { passive: true });
          this.onScroll();
        }

        disconnectedCallback() {
          window.removeEventListener('scroll', this.onScroll);
          window.removeEventListener('resize', this.onResize);
          document.body.classList.remove('rejuv-has-sticky');
        }

        onScroll() {
          const show = window.scrollY > this.threshold;
          if (show === this.shown) return;
          this.shown = show;
          this.bar.classList.toggle('is-visible', show);
          this.bar.setAttribute('aria-hidden', show ? 'false' : 'true');

          // Let fixed third-party launchers move above the bar rather than
          // padding the bar out to clear them.
          document.body.classList.toggle('rejuv-has-sticky', show);
          if (show) this.publishClearance();
          else document.documentElement.style.removeProperty('--rejuv-launcher-h');
        }

        /*
         * Find a third-party launcher pinned to the bottom of the viewport and
         * publish its height so the bar can lift clear of it. Probing by point
         * rather than by class avoids hard-coding any one app's markup.
         */
        publishClearance() {
          let height = 0;
          try {
            const y = window.innerHeight - 4;
            const xs = [window.innerWidth - 60, window.innerWidth - 160, window.innerWidth / 2];
            xs.forEach((x) => {
              document.elementsFromPoint(x, y).forEach((node) => {
                if (height) return;
                if (node === this.bar || this.bar.contains(node) || this.contains(node)) return;
                const style = getComputedStyle(node);
                if (style.position !== 'fixed') return;
                const box = node.getBoundingClientRect();
                // A launcher, not a full-screen overlay.
                if (box.height > 0 && box.height <= 120 && box.width < window.innerWidth * 0.9) {
                  height = Math.ceil(box.height);
                }
              });
            });
          } catch (e) {
            height = 0;
          }
          document.documentElement.style.setProperty('--rejuv-launcher-h', height + 'px');
        }
      }
    );
  }

  if (!customElements.get('rejuv-buy')) {
    customElements.define(
      'rejuv-buy',
      class RejuvBuy extends HTMLElement {
        connectedCallback() {
          try {
            this.variants = JSON.parse(this.querySelector('[data-rejuv-variants]').textContent);
          } catch (e) {
            this.variants = [];
          }
          if (!this.variants.length) return;

          this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
          this.productUrl = this.dataset.productUrl || '';
          this.addToCartText = this.dataset.addToCartText || 'Add to Cart';
          this.soldOutText = this.dataset.soldOutText || 'Sold out';
          this.unavailableText = this.dataset.unavailableText || 'Unavailable';

          this.form = this.querySelector('form');
          this.idInput = this.querySelector('[name="id"]');
          this.planInput = this.querySelector('[name="selling_plan"]');
          this.qtyInput = this.querySelector('[data-rejuv-qty]');
          this.submitButton = this.querySelector('.rejuv-cta');
          this.submitLabel = this.submitButton && this.submitButton.querySelector('span');

          // The gallery sits outside this element, alongside it in the section.
          const media = this.closest('.rejuv-buy') || document;
          this.videoSlots = Array.from(media.querySelectorAll('[data-rejuv-video-for]'));
          this.variantThumb = media.querySelector('[data-rejuv-variant-thumb] img');
          this.factsImage = this.querySelector('[data-rejuv-facts-image]');
          this.factsText = this.querySelector('[data-rejuv-facts-text]');
          try {
            const map = this.querySelector('[data-rejuv-facts-map]');
            this.factsMap = map ? JSON.parse(map.textContent) : {};
          } catch (e) {
            this.factsMap = {};
          }

          this.optionButtons = Array.from(this.querySelectorAll('[data-rejuv-option]'));
          this.planButtons = Array.from(this.querySelectorAll('[data-rejuv-plan]'));

          const initialId = this.dataset.selectedVariant;
          this.current =
            this.variants.find((v) => String(v.id) === String(initialId)) || this.variants[0];
          this.selection = (this.current.options || []).slice();
          this.plan = this.planButtons.length
            ? this.planButtons[0].dataset.rejuvPlan
            : '';

          // Respect a pre-selected plan button if the section marked one.
          const preselectedPlan = this.planButtons.find(
            (b) => b.getAttribute('aria-pressed') === 'true'
          );
          if (preselectedPlan) this.plan = preselectedPlan.dataset.rejuvPlan;

          this.optionButtons.forEach((button) => {
            button.addEventListener('click', () => {
              const index = parseInt(button.dataset.rejuvOptionIndex, 10);
              this.selection[index] = button.dataset.rejuvOption;
              this.resolveVariant();
              this.render();
            });
          });

          this.planButtons.forEach((button) => {
            button.addEventListener('click', () => {
              this.plan = button.dataset.rejuvPlan;
              this.render();
            });
          });

          this.querySelectorAll('[data-rejuv-qty-step]').forEach((button) => {
            button.addEventListener('click', () => {
              const step = parseInt(button.dataset.rejuvQtyStep, 10) || 1;
              this.setQuantity(this.quantity() + step);
            });
          });

          if (this.qtyInput) {
            this.qtyInput.addEventListener('change', () => this.setQuantity(this.quantity()));
          }

          this.render();
        }

        quantity() {
          const value = parseInt(this.qtyInput && this.qtyInput.value, 10);
          return isNaN(value) ? 1 : value;
        }

        setQuantity(value) {
          const next = Math.min(99, Math.max(1, value));
          if (this.qtyInput) this.qtyInput.value = next;
          this.render();
        }

        /*
         * Pick the variant matching the current option selection. When the
         * combination does not exist we keep the selection (so the button
         * stays visibly chosen) and fall back to the closest variant that
         * matches the option the shopper just changed.
         */
        resolveVariant() {
          const exact = this.variants.find((variant) =>
            (variant.options || []).every((value, i) => value === this.selection[i])
          );

          if (exact) {
            this.current = exact;
            this.unavailableCombination = false;
          } else {
            this.unavailableCombination = true;
          }

          if (exact) this.updateUrl(exact.id);
        }

        updateUrl(id) {
          if (!this.productUrl || !window.history || !window.history.replaceState) return;
          window.history.replaceState({}, '', this.productUrl + '?variant=' + id);
        }

        /* Unit price for the current variant under the current plan. */
        unitPrice() {
          if (this.plan && this.current.plans && this.current.plans[this.plan] != null) {
            return this.current.plans[this.plan];
          }
          return this.current.price;
        }

        planPrice(planId) {
          if (planId && this.current.plans && this.current.plans[planId] != null) {
            return this.current.plans[planId];
          }
          return this.current.price;
        }

        /*
         * Show the flavour's supplement facts panel when one is attached to the
         * variant, falling back to the metafield-rendered table when it isn't.
         */
        updateFactsImage(variant) {
          if (!this.factsImage) return;

          // A flavour-keyed panel wins; an image attached to the variant is the
          // fallback for stores where a variant can hold more than one image.
          let panel = null;
          (variant.options || []).forEach((value) => {
            const key = String(value).toLowerCase().trim();
            if (this.factsMap && this.factsMap[key]) panel = this.factsMap[key];
          });
          if (!panel && variant.facts_image) {
            panel = { src: variant.facts_image, srcset: variant.facts_srcset, alt: variant.facts_alt };
          }

          if (panel && panel.src) {
            if (this.factsImage.getAttribute('src') !== panel.src) {
              this.factsImage.setAttribute('src', panel.src);
              if (panel.srcset) {
                this.factsImage.setAttribute('srcset', panel.srcset);
              } else {
                this.factsImage.removeAttribute('srcset');
              }
            }
            this.factsImage.setAttribute('alt', panel.alt || '');
            this.factsImage.hidden = false;
            if (this.factsText) this.factsText.hidden = true;
          } else {
            this.factsImage.hidden = true;
            if (this.factsText) this.factsText.hidden = false;
          }
        }

        /* Point the first thumbnail at the selected variant's own image. */
        updateVariantThumb(variant) {
          if (!this.variantThumb || !variant.image) return;
          if (this.variantThumb.getAttribute('src') === variant.image) return;
          this.variantThumb.setAttribute('src', variant.image);
          if (variant.image_srcset) {
            this.variantThumb.setAttribute('srcset', variant.image_srcset);
          } else {
            this.variantThumb.removeAttribute('srcset');
          }
          this.variantThumb.setAttribute('alt', variant.image_alt || '');
        }

        /*
         * Show the video whose option value matches the selection, falling back
         * to the default slot. Videos are mounted up front and toggled, so the
         * hidden ones are paused rather than reloaded.
         */
        updateHeroVideo(variant) {
          if (this.videoSlots.length < 2) return;
          const chosen = (variant.options || []).map((value) => String(value).toLowerCase().trim());

          let active = this.videoSlots.find((slot) => slot.dataset.rejuvVideoFor === '');
          this.videoSlots.forEach((slot) => {
            const key = slot.dataset.rejuvVideoFor;
            if (key && chosen.indexOf(key) !== -1) active = slot;
          });
          if (active === this.activeVideoSlot) return;
          this.activeVideoSlot = active;

          this.videoSlots.forEach((slot) => {
            const on = slot === active;
            slot.hidden = !on;
            const video = slot.querySelector('video');
            if (!video) return;
            if (on) {
              video.muted = true;
              video.defaultMuted = true;
              if (video.preload !== 'auto') video.preload = 'auto';
              const playing = video.play();
              if (playing && playing.catch) playing.catch(() => {});
            } else {
              video.pause();
            }
          });
        }

        render() {
          const variant = this.current;
          const purchasable = variant.available && !this.unavailableCombination;
          const unit = this.unitPrice();

          if (this.idInput) this.idInput.value = variant.id;
          if (this.planInput) this.planInput.value = this.plan || '';

          this.optionButtons.forEach((button) => {
            const index = parseInt(button.dataset.rejuvOptionIndex, 10);
            const value = button.dataset.rejuvOption;
            button.setAttribute('aria-pressed', this.selection[index] === value ? 'true' : 'false');

            // Grey out values that cannot be combined with the other choices.
            const candidate = this.selection.slice();
            candidate[index] = value;
            const match = this.variants.find((v) =>
              (v.options || []).every((o, i) => o === candidate[i])
            );
            button.dataset.unavailable = match && match.available ? 'false' : 'true';

            const priceEl = button.querySelector('[data-rejuv-option-price]');
            if (priceEl && match) priceEl.textContent = formatMoney(match.price, this.moneyFormat);
          });

          this.planButtons.forEach((button) => {
            const planId = button.dataset.rejuvPlan;
            button.setAttribute('aria-pressed', planId === this.plan ? 'true' : 'false');
            const priceEl = button.querySelector('[data-rejuv-plan-price]');
            if (priceEl) priceEl.textContent = formatMoney(this.planPrice(planId), this.moneyFormat);
          });

          this.querySelectorAll('[data-rejuv-price]').forEach((el) => {
            el.textContent = formatMoney(unit, this.moneyFormat);
          });

          // The sticky bar sits outside this element so it can be position:fixed
          // without inheriting the sticky info column's containing block.
          document.querySelectorAll('[data-rejuv-sticky-price]').forEach((el) => {
            el.textContent = formatMoney(unit, this.moneyFormat);
          });

          this.querySelectorAll('[data-rejuv-compare-price]').forEach((el) => {
            const show = variant.compare_at_price && variant.compare_at_price > unit;
            el.textContent = show ? formatMoney(variant.compare_at_price, this.moneyFormat) : '';
            el.hidden = !show;
          });

          this.querySelectorAll('[data-rejuv-supply]').forEach((el) => {
            if (variant.supply) el.textContent = variant.supply;
          });

          this.updateVariantThumb(variant);
          this.updateFactsImage(variant);
          this.updateHeroVideo(variant);

          const minus = this.querySelector('[data-rejuv-qty-step="-1"]');
          if (minus) minus.disabled = this.quantity() <= 1;

          if (this.submitButton) {
            this.submitButton.setAttribute('aria-disabled', purchasable ? 'false' : 'true');
            this.submitButton.disabled = !purchasable;
          }

          if (this.submitLabel) {
            if (this.unavailableCombination) {
              this.submitLabel.textContent = this.unavailableText;
            } else if (!variant.available) {
              this.submitLabel.textContent = this.soldOutText;
            } else {
              this.submitLabel.textContent =
                this.addToCartText +
                ' — ' +
                formatMoney(unit * this.quantity(), this.moneyFormat);
            }
          }
        }
      }
    );
  }
})();
